import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Download, ShoppingBag, ArrowRight } from "lucide-react";

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Purchases() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/remedies/purchases").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const download = async (p) => {
    setBusy(p.product_id);
    try {
      const { data: meta } = await api.get(`/remedies/access/${p.product_id}`);
      if (meta.configuration_required || !meta.download_url) {
        toast.info("Your content link will be added shortly.");
        return;
      }
      const resp = await api.get(meta.download_url, { responseType: "blob" });
      const cd = resp.headers?.["content-disposition"] || "";
      const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const filename = nameMatch ? decodeURIComponent(nameMatch[1]) : (meta.title || p.title || "remedy");
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.data.type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Library</p>
        <h1 className="font-serif text-4xl tracking-tight mb-8">My purchases</h1>

        {items === null ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="purchases-empty">
            <ShoppingBag className="mx-auto mb-4 text-muted-foreground/60" strokeWidth={1.5} />
            No purchases yet.
            <div className="mt-5"><Link to="/remedies"><Button className="rounded-full bg-primary text-primary-foreground font-semibold" data-testid="browse-remedies-btn">Browse the Remedies store <ArrowRight size={16} strokeWidth={1.5} /></Button></Link></div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="purchases-list">
            {items.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4" data-testid={`purchase-${p.product_id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.category}{p.amount ? ` · ${inr(p.amount)}` : ""}{p.purchased_at ? ` · ${new Date(p.purchased_at).toLocaleDateString()}` : ""}</p>
                </div>
                {p.has_content
                  ? <Button onClick={() => download(p)} disabled={busy === p.product_id} className="rounded-full bg-primary text-primary-foreground font-semibold whitespace-nowrap" data-testid={`purchase-download-${p.product_id}`}>
                      {busy === p.product_id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} strokeWidth={1.5} />} Download
                    </Button>
                  : <span className="text-xs text-muted-foreground whitespace-nowrap">Link coming soon</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}